import React from 'react';
import { Checkbox, FormControlLabel, MenuItem, Select, Switch, TextField } from '@mui/material';
import classnames from 'classnames';
import classes from './PaginationConfig.module.scss';
import { ITERATION_TYPES, getVisibleIterationFields, type IterationField } from './iterationFields';
import { extractAtPath, parseMaybeJson } from './jsonPath';

/**
 * Redesigned pagination configuration (prototype visual; existing data model).
 *
 * Renders the legacy 13-type iteration model from `iterationFields.ts` inside the
 * prototype's compact popover layout. USABILITY-ONLY: it reads `stageConfig` and
 * writes via `onChange(key, value)` which the caller forwards to the existing
 * `onAPIConfigChanged(key, value, stageIndex)` — so the produced config is identical
 * to the legacy form.
 *
 * Not yet wired into the live step list (RestApiStepList delegates pagination to the
 * legacy ConfigureAPIStage). This is the ready-to-integrate building block for the
 * inner-control restyle phase — see docs/rest-api-source-redesign/README.md.
 */

export interface PaginationConfigProps {
  stageIndex: number;
  stageConfig: Record<string, any>;
  /** this step's probe response, used to validate path params against the response */
  apiResponse?: any;
  /** caller forwards to onAPIConfigChanged(key, value, stageIndex) */
  onChange: (key: string, value: any) => void;
  /**
   * Hide the built-in "This API is paginated" toggle. Used by the gear-popover
   * shell (StepPaginationControl), which owns the toggle in the step header and
   * renders only the settings body here. When true, the settings always render.
   */
  hideToggle?: boolean;
}

const coerce = (field: IterationField, raw: string): string | number => {
  if (field.type === 'number') {
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? '' : n;
  }
  return raw;
};

/** Preview a resolved value compactly (objects/arrays as JSON, truncated). */
const previewValue = (value: any): string => {
  const text = typeof value === 'string' ? value : JSON.stringify(value);
  return text.length > 60 ? `${text.slice(0, 60)}…` : text;
};

export const PaginationConfig: React.FC<PaginationConfigProps> = ({
  stageIndex,
  stageConfig,
  apiResponse,
  onChange,
  hideToggle = false,
}) => {
  const isPaginated: boolean = hideToggle || !!stageConfig.isPaginated;
  const iterationType: string = stageConfig['iteration.type'] || '';

  const handleTogglePaginated = (checked: boolean) => {
    // Legacy only toggles isPaginated; static.url is applied at serialize time.
    onChange('isPaginated', checked);
  };

  const visibleFields = getVisibleIterationFields(iterationType, stageIndex);

  const responseContent = apiResponse?.apiResponseContent;

  /**
   * For path-like values (e.g. "$.page"), evaluate against this step's response
   * and report the resolved value or that it doesn't match — so the user knows
   * their entry is correct before saving.
   */
  const renderPathResult = (raw: any) => {
    const value = typeof raw === 'string' ? raw.trim() : '';
    if (!value || !/^\$/.test(value) || !responseContent) return null;
    const resolved = extractAtPath(parseMaybeJson(responseContent), value);
    if (resolved === undefined) {
      return (
        <span className={classnames(classes.pathResult, classes.pathInvalid)}>
          No match in response
        </span>
      );
    }
    return (
      <span className={classnames(classes.pathResult, classes.pathValid)}>
        = {previewValue(resolved)}
      </span>
    );
  };

  const renderField = (field: IterationField) => {
    const value = stageConfig[field.key];

    if (field.type === 'checkbox') {
      return (
        <FormControlLabel
          key={field.key}
          className={classes.checkboxField}
          control={
            <Checkbox
              size="small"
              checked={!!value}
              onChange={(e) => onChange(field.key, e.target.checked)}
              inputProps={{ 'aria-label': field.label } as any}
              data-testid={`pagination-field-${field.key}`}
            />
          }
          label={field.label}
        />
      );
    }

    if (field.type === 'select') {
      return (
        <div key={field.key} className={classes.field}>
          <label className={classes.fieldLabel}>{field.label}</label>
          <Select
            size="small"
            fullWidth
            value={value ?? field.default ?? ''}
            onChange={(e) => onChange(field.key, e.target.value)}
            data-testid={`pagination-field-${field.key}`}
            inputProps={{ 'aria-label': field.label } as any}
          >
            {(field.options ?? []).map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </div>
      );
    }

    return (
      <div key={field.key} className={classes.field}>
        <label className={classes.fieldLabel}>
          {field.optional ? `(Optional) ${field.label}` : field.label}
        </label>
        <TextField
          size="small"
          fullWidth
          type={field.type === 'number' ? 'number' : 'text'}
          value={value ?? ''}
          placeholder={
            field.placeholder ?? (field.default != null ? String(field.default) : undefined)
          }
          onChange={(e) => onChange(field.key, coerce(field, e.target.value))}
          inputProps={{ 'aria-label': field.label, 'data-testid': `pagination-field-${field.key}` }}
        />
        {renderPathResult(value)}
      </div>
    );
  };

  return (
    <div className={classes.root} data-testid="pagination-config">
      {!hideToggle && (
        <FormControlLabel
          className={classes.paginateToggle}
          control={
            <Switch
              checked={isPaginated}
              onChange={(e) => handleTogglePaginated(e.target.checked)}
              data-testid="pagination-toggle"
            />
          }
          label="This API is paginated"
        />
      )}

      {isPaginated && (
        <div className={classes.settings} data-testid="pagination-settings">
          <div className={classes.field}>
            <label className={classes.fieldLabel}>Pagination Type</label>
            <Select
              size="small"
              fullWidth
              displayEmpty
              value={ITERATION_TYPES.some((t) => t.value === iterationType) ? iterationType : ''}
              onChange={(e) => onChange('iteration.type', e.target.value)}
              data-testid="pagination-type-select"
              inputProps={{ 'aria-label': 'Pagination Type' } as any}
            >
              <MenuItem value="" disabled>
                Select pagination type
              </MenuItem>
              {ITERATION_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </div>

          <div className={classes.fieldGrid}>{visibleFields.map(renderField)}</div>
        </div>
      )}
    </div>
  );
};

export default PaginationConfig;
